import { Bolton, OrderCredit, Plan, PortInPoolItem } from '@smarty-nx/data'

import {
  buildAddonProductData,
  buildAddonTransactionData,
} from './buildEventData'
import { buildCartData, buildProductData, buildTransactionData } from './index'
import {
  DATA_LAYER_EVENTS,
  DATA_LAYER_JOURNEY_TYPES,
  ORDER_TYPES,
  PORT_IN_STATUS,
} from '../constants'
import {
  buildCart,
  buildGroupInfo,
  buildUser,
  buildUserWithOrderType,
  moduleEvent,
  updateDmpgSmdl,
} from '../services'
import {
  AddonProductObject,
  DataLayerEvent,
  FormEventProps,
  ProductObject,
  SmdlScreen,
} from '../types'
import { getCodeType } from '@smarty-nx/code-utils'

/** Pushes an event to the data layer */
export const triggerEvent = (event: DataLayerEvent) => {
  window.smDl.events.push(event)
}

// Accepts event name (required) and formData - form name and fields
export const triggerFormEvent = ({
  eventName,
  formData,
  error = null,
  userObject,
}: FormEventProps) => {
  triggerEvent({
    event: eventName,
    form: {
      id: formData.id,
      ...(formData.name && { name: formData.name }),
      fields: formData.fields || [],
      // Defaulted errors to an empty array in case of numerous errors.
      // single strings get passed in as ['string']
      error,
    },
    user: userObject,
  })
}

/** v2 cart event function - builds cart based on v2 Plan structure */
export const triggerCartLoadEvent = (
  plan: Plan,
  checkoutStep: number,
  orderType?: ORDER_TYPES,
  checkoutType?: DATA_LAYER_JOURNEY_TYPES,
  accountType?: string,
  isUlfp?: boolean
) => {
  // Lydia said that for this event we do not need GMs/SIMs number data at all, so
  // "membersNumber" omitted
  triggerEvent({
    event: DATA_LAYER_EVENTS.CART_LOAD,
    // uses the old buildCart function
    cart: buildCartData({
      plan,
      checkoutStep,
      orderType,
      checkoutType,
      isUlfp,
    }),
    groupInfo: buildGroupInfo(null, accountType),
  })
}

export const triggerRegistrationSuccessEvent = (
  method = 'email',
  registrations = 'true'
) => {
  triggerEvent({
    event: DATA_LAYER_EVENTS.REGISTRATION_SUCCESS,
    method,
    registrations,
    user: buildUser(),
    groupInfo: buildGroupInfo(),
  })
}

/**
 * Function to trigger an event with cart data - mostly used on order journeys.
 * The buildCart function takes data attributes from components
 */
export const triggerOrderEvent = (eventName: string) => {
  triggerEvent({
    event: eventName,
    /** uses the old buildCart function for now - only on PayPal journey */
    cart: buildCart(document),
  })
}

/**
 * Function to trigger an event with cart data - mostly used on order journeys.
 * The buildCart function takes data attributes from components
 * PRODUCT_VIEW / ADD_TO_CART
 */
export const triggerProductEvent = (
  eventName: string,
  plan: Plan,
  membersNumber?: number,
  orderType?: ORDER_TYPES,
  isULFP = false
) => {
  triggerEvent({
    event: eventName,
    order_type: orderType,
    // uses the old buildCart function
    product: buildProductData(plan, membersNumber, orderType, isULFP),
  })
}

/**
 * Function to trigger an event with add-on cart data.
 */
export const triggerAddonProductEvent = (
  eventName: string,
  addon: Bolton,
  orderType?: ORDER_TYPES
) => {
  triggerEvent({
    event: eventName,
    order_type: orderType,
    product: buildAddonProductData(addon),
  })
}

/**
 * Function to trigger an event with custom cart data.
 */
export const triggerCustomProductEvent = (
  eventName: string,
  product: ProductObject | AddonProductObject,
  order_type?: ORDER_TYPES
) => {
  triggerEvent({
    event: eventName,
    product,
    order_type,
  })
}

/**
 * New function to trigger a manual screen load event.
 * It accepts a screen object with page name and type.
 * We can take this from PAGE_CONSTANTS or the journey state objects in
 * journeyStages.js (we should replace these with PAGE_CONSTANTS as we rebuild)
 */
export const triggerScreenLoadEvent = (screenObject: SmdlScreen) => {
  const event = {
    event: DATA_LAYER_EVENTS.SCREEN_LOAD,
    screen: {
      name: screenObject.name,
      type: screenObject.type,
      ...(screenObject.order_type && { order_type: screenObject.order_type }),
      ...(screenObject.port_journey_type && {
        port_journey_type: screenObject.port_journey_type,
      }),
      ...(screenObject.lead_generator && {
        lead_generator: screenObject.lead_generator,
      }),
    },
    user: {
      ...buildUser(),
    },
    groupInfo: buildGroupInfo(null, screenObject?.accountType),
    ...(screenObject.sim_type ? { sim_type: screenObject.sim_type } : {}),
  }
  triggerEvent(event)
}

/** Build and trigger an event with no extra data */
export const triggerSimpleEvent = (eventName: string) => {
  triggerEvent({ event: eventName })
}

// TODO: This one more specific but 'addDmpg' is more universal; usage only in dashboard. Let's
//  cleanup
export const triggerTransactionEvent = ({
  id,
  orderCredit,
  plan,
  checkoutType,
  portInOption,
  membersNumber = 0,
  orderType,
  isUlfp = false,
}: {
  id: string | null
  orderCredit: OrderCredit
  plan: Plan
  checkoutType: DATA_LAYER_JOURNEY_TYPES
  portInOption?: PORT_IN_STATUS
  membersNumber?: number
  orderType?: ORDER_TYPES
  isUlfp?: boolean
}) => {
  triggerEvent({
    event: DATA_LAYER_EVENTS.TRANSACTION_SUCCESS,
    transaction: buildTransactionData({
      id,
      orderCredit,
      plan,
      checkoutType,
      portInOption,
      membersNumber,
      orderType,
      isUlfp,
    }),
  })
}

export const triggerPlanCardModuleEvent = (
  parentElement: Document | HTMLElement,
  slug: string
) => {
  const output = moduleEvent(parentElement, slug)
  updateDmpgSmdl(output)
}

export const triggerAddonTransactionEvent = (
  id: string,
  orderCredit: OrderCredit,
  bolton: Bolton,
  order_type?: ORDER_TYPES
) => {
  triggerEvent({
    event: DATA_LAYER_EVENTS.TRANSACTION_SUCCESS,
    ...buildAddonTransactionData(id, orderCredit, bolton),
    order_type,
  })
}

// TODO: let's leave only this one or 'triggerGroupMemberPortingEvent'
export const triggerPortingEvent = (
  eventName: string,
  option: string,
  status: string,
  portJourneyType?: string,
  orderType?: string
) => {
  triggerEvent({
    event: eventName,
    ...(portJourneyType ? { port_journey_type: portJourneyType } : {}),
    user: {
      ...buildUserWithOrderType(orderType),
      porting: {
        option,
        status,
      },
    },
  })
}

export const triggerGroupMemberPortingEvent = (
  eventName: string,
  option: string,
  status: string,
  portJourneyType?: string,
  orderType?: string,
  includeGroupInfo = false
) => {
  const event = {
    event: eventName,
    ...(portJourneyType && { port_journey_type: portJourneyType }),
    user: {
      ...buildUserWithOrderType(orderType),
      // TODO: check/ask how we determine when to trigger "GroupMemberPorting" vs
      //  'triggerPortingEvent' ? And do we need this "groupMember" level at all, instead of just "porting"?
      porting: {
        groupMember: {
          option,
          status,
        },
      },
    },
  }

  if (includeGroupInfo) {
    Object.assign(event, { groupInfo: buildGroupInfo() })
  }

  triggerEvent(event)
}

export const triggerAddonModuleEvent = (
  parentElement: Document | HTMLElement,
  slug: string
) => {
  const output = moduleEvent(parentElement, slug)
  updateDmpgSmdl(output)
}

// TODO: Only actual for real PORT-IN journey or candidate for cleanup
export const triggerGroupPortingPrefEvent = (
  portInPool: PortInPoolItem[] | null,
  orderType?: string,
  portJourneyType = 'before-activation'
) => {
  triggerEvent({
    event: DATA_LAYER_EVENTS.SET_PORTING_PREF,
    port_journey_type: portJourneyType,
    user: {
      ...buildUserWithOrderType(orderType),
      // TODO: we need to re-work this structure, as it should show only selected option by
      //  GO|GM not a port-in pool items collection
      porting: {
        groupMember: portInPool
          ? portInPool.map((item) => ({
              option: `port-in-${getCodeType(item.code)}`,
              status: 'requested-port-in',
            }))
          : {
              option: 'port-in-later',
              status: 'not-ported',
            },
      },
    },
  })
}

export const triggerGroupInviteEvent = (
  inviteType: string,
  invitations: number,
  groupSize: number
) => {
  const state = JSON.parse(localStorage.getItem('state') || '{}')

  triggerEvent({
    event: DATA_LAYER_EVENTS.GROUP_INVITE,
    groupInfo: {
      inviteType,
      group: {
        id: state.groupId || null,
        groupSize,
        invitations,
      },
    },
  })
}
